/**
 * Aadhar OTP Actions
 * Handles OTP sending and verification for viewing encrypted Aadhar numbers
 */

import apiClient from "../services/apiClient";
import { URL_CONFIG } from "../config/urlConfig";

/**
 * Send OTP to admin email for viewing encrypted Aadhar
 * @param {string} email - Admin email address
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const sendAadharOtpAction = async (email) => {
  try {
    const response = await apiClient.get(
      `${URL_CONFIG.ATP_API_BASE_URL}api/Onboarding/send-otp-for-encrypted-Value`,
      {
        params: { email },
      },
    );

    if (response.status === 200) {
      return {
        success: true,
        message: response.data?.message || "OTP sent successfully",
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Failed to send OTP",
      };
    }
  } catch (error) {
    console.error("[sendAadharOtpAction] Error:", error);
    return {
      success: false,
      message:
        error.response?.data?.message || error.message || "Failed to send OTP",
    };
  }
};

/**
 * Verify OTP and get original (decrypted) Aadhar value
 * @param {Object} params - Verification parameters
 * @param {number} params.entityPrimaryId - Entity primary ID
 * @param {string} params.documentName - Document name (e.g., "aadharCardNumber")
 * @param {string} params.email - Admin email address
 * @param {string} params.otp - OTP code
 * @returns {Promise<{success: boolean, message: string, originalValue: string}>}
 */
export const verifyAadharOtpAction = async ({
  entityPrimaryId,
  documentName,
  email,
  otp,
}) => {
  try {
    const payload = {
      entityPrimaryId: parseInt(entityPrimaryId, 10) || 0,
      documentName: documentName || "",
      email: email || "",
      otp: otp || "",
    };

    const response = await apiClient.post(
      `${URL_CONFIG.ATP_API_BASE_URL}api/Onboarding/get-original-value`,
      payload,
    );

    if (response.status === 200 && response.data) {
      return {
        success: true,
        message: "OTP verified successfully",
        originalValue: response.data.originalValue || response.data,
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Invalid OTP",
        originalValue: null,
      };
    }
  } catch (error) {
    console.error("[verifyAadharOtpAction] Error:", error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        "Failed to verify OTP",
      originalValue: null,
    };
  }
};

/**
 * Map field names to API document names
 * @param {string} fieldName - Internal field name
 * @returns {string} - API document name
 */
export const getDocumentNameForField = (fieldName) => {
  const fieldToDocumentMap = {
    // KYC-detail page (UploadDetails) field names → API document names
    doc_aadhar_number: "aadharCardNumber",
    doc_aadharBoth_numbers: "aadharNumberOfBothPartners",
    doc_aadharTrustee_number: "trusteeAadharNumber",
    doc_directorAadhar_number: "directorAadharNumber",
    // All List page (EditClientModal) field names — already match API names
    aadharCardNumber: "aadharCardNumber",
    aadharNumberOfBothPartners: "aadharNumberOfBothPartners",
    aadharNumberOfTrustee: "trusteeAadharNumber",
    directorAadharNumber: "directorAadharNumber",
  };

  return fieldToDocumentMap[fieldName] || fieldName;
};
